import { Box, Typography } from '@atgfr8/atg-ui-tailwind';
import { BasicModal, Button, ToastType } from '@components';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import CommonFileUpload from '@components/FileUpload/CommonFileUpload';
import { prospectUrls } from '@constants/urls';
import { closeModal } from '@store/modals';
import { bulkImportProspects } from '@store/prospect';
import { CheckIcon, DownloadIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isFullAdmin } from '@utils/utils';
import { IMPORT_PROSPECT_MODAL_ID } from '@views/order/constants';
import { size } from 'lodash';
import { Accessor, Component, createSignal, For, Setter, Show } from 'solid-js';

type ImportProspectModalProps = {
  refetch: Accessor<boolean>;
  setRefetch: Setter<boolean>;
};

export const ImportProspectModal: Component<ImportProspectModalProps> = (
  props,
) => {
  const [imported, setImported] = createSignal<string[]>([]);
  const [notimported, setNotImported] = createSignal<
    {
      name: string;
      reason: string;
    }[]
  >([]);

  const handleFileChange = async (file: File) => {
    setImported([]);
    setNotImported([]);
    const res = await bulkImportProspects(file, [
      CHANGE_TRACKING_SOURCES.prospects.bulkImport,
    ]);
    if (res && res.isSuccess) {
      handleToast(ToastType.Success, 'Prospects Imported Successfully');
      setImported(res.value.imported);
      setNotImported(res.value.notImported);
      if (size(res.value.imported) > 0) {
        props.setRefetch(true);
      }
    }
  };

  const handleCloseModal = () => {
    setImported([]);
    setNotImported([]);
    closeModal(IMPORT_PROSPECT_MODAL_ID);
  };

  return (
    <BasicModal
      id={IMPORT_PROSPECT_MODAL_ID}
      title="Bulk Prospect Import"
      width="600px"
      footer={false}
      onClose={handleCloseModal}
    >
      <CommonFileUpload
        height={5}
        uploadedFiles={[]}
        acceptedFileTypes={['csv']}
        shouldUploadOnSelect
        apiCallBackFn={handleFileChange}
        maxFileSize={5242880}
        fileRestrictionMessage="From here you can bulk import your prospects. The file must be a .csv and 5MB or less."
        isAdmin={isFullAdmin()}
      />
      <Box display="flex" justifyContent="end" mt={2}>
        <Button
          startIcon={<DownloadIcon size={20} />}
          label="Download Sample File"
          variant="contained"
          type="button"
          onClick={() => {
            window.open(prospectUrls.sampleDocumentLink(), '_blank');
          }}
          dataTestId={formatDataTestId('Download Sample File', 'button')}
        />
      </Box>
      <Show when={imported().length > 0}>
        <Box class="max-h-60 overflow-y-auto">
          <Typography variant="body1" class="text-[#3c763d]">
            Imported:
          </Typography>
          <For each={imported()}>
            {(imported) => (
              <Box class="text-[#3c763d] ">
                <strong class="flex items-center">
                  <CheckIcon class="mr-2 size-5!" /> {imported}
                </strong>
              </Box>
            )}
          </For>
        </Box>
      </Show>
      <Show when={notimported().length > 0}>
        <Box class="max-h-60 overflow-y-auto">
          <Typography variant="body1" class="text-[#a94442]">
            Not Imported:
          </Typography>
          <For each={notimported()}>
            {(notimport) => (
              <Box class="text-[#a94442]">
                <strong>{notimport.name} :</strong> {notimport.reason}
              </Box>
            )}
          </For>
        </Box>
      </Show>
    </BasicModal>
  );
};
